import React from "react";
import { useTranslation } from "react-i18next";
import { Trash2Icon } from "lucide-react";
import { formatAmount } from "../../../api/DataTypeUtils";
import { RecordBadge, DealsSVG } from "../../component/picker-shared";

export type DealRow = {
  id: string | number;
  name: string;
  avatarUrl?: string;
  assignees?: { name?: string; avatarUrl?: string }[];
  bucket?: any;
  estimatedRevenue?: number;
  estimatedProbability?: number;
  currency?: string;
};

function AssigneeAvatars({ list = [] as DealRow["assignees"] }) {
  if (!list || list.length === 0) return null;
  const displayList = list.slice(0, 2);
  const remainingCount = list.length > 2 ? list.length - 2 : 0;

  return (
    <div className="flex items-center gap-1.5 min-w-0">
      {displayList.map((a, idx) => (
        <div key={idx} className="flex items-center gap-1.5 min-w-0">
          {a?.avatarUrl ? (
            <img
              src={a.avatarUrl}
              alt={a.name || "avatar"}
              className="w-5 h-5 rounded-full shrink-0"
            />
          ) : (
            <div className="w-5 h-5 rounded-full bg-gray-200 shrink-0" />
          )}
          {a?.name && <span className="text-xs truncate">{a.name}</span>}
        </div>
      ))}
      {remainingCount > 0 && <span className="text-xs text-gray-500">+{remainingCount}</span>}
    </div>
  );
}

function Row({
  item,
  onOpen,
  onUnlink,
}: {
  item: DealRow;
  onOpen?: (id: string | number) => void;
  onUnlink?: (id: string | number) => void;
}) {
  const { i18n } = useTranslation();

  return (
    <div className="group flex items-center justify-between gap-2 w-full py-2">
      {/* Name & Avatar */}
      <div className="flex items-center gap-2 flex-2 min-w-0">
        {item.avatarUrl ? (
          <img src={item.avatarUrl} alt="" className="w-5 h-5 rounded-full shrink-0" />
        ) : (
          <RecordBadge Icon={DealsSVG} shape="square" className="w-5 h-5" iconScale={1.6} />
        )}
        <div
          className="truncate text-sm font-medium text-gray-900 cursor-pointer hover:underline"
          title={item.name}
          onClick={() => onOpen?.(item.id)}
        >
          {item.name}
        </div>
      </div>

      {/* Bucket */}
      <div className="flex-1">
        <div className="w-max flex items-center gap-2 py-1 px-2 rounded-full bg-gray-200">
          <div className="w-2 h-2 rounded-full bg-gray-600"></div>
          <div className="text-xs text-gray-600">{item.bucket?.name}</div>
        </div>
      </div>

      {/* Assignees */}
      <div className="flex-2">
        <AssigneeAvatars list={item.assignees} />
      </div>

      {/* Estimated Revenue */}
      <div className="flex-1">
        {formatAmount(i18n.language, item.estimatedRevenue || 0, item.currency, "0$")}
      </div>

      {/* Estimated Probability */}
      <div className="flex-1">{Math.round(item.estimatedProbability || 0)}%</div>

      {/* Actions (show on hover) */}
      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100">
        <button
          type="button"
          onClick={() => onUnlink?.(item.id)}
          className="p-1 rounded hover:bg-gray-100"
          aria-label="Unlink"
          title="Unlink"
        >
          <Trash2Icon className="w-4 h-4 text-red-500" />
        </button>
      </div>
    </div>
  );
}

interface DealsSectionProps {
  title: string;
  action: React.ReactNode;
  items: DealRow[];
  onOpen?: (id: string | number) => void;
  onUnlink?: (id: string | number) => void;
}

export default function DealsSection({
  title,
  action,
  items,
  onOpen,
  onUnlink,
}: DealsSectionProps) {
  return (
    <div className="w-full flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between pb-2 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {action}
      </div>

      {/* List */}
      <div className="flex flex-col divide-y divide-gray-100">
        {items.map((item) => (
          <Row key={item.id} item={item} onOpen={onOpen} onUnlink={onUnlink} />
        ))}
        {items.length === 0 && (
          <div className="py-8 text-center text-sm text-gray-600">No items linked yet</div>
        )}
      </div>
    </div>
  );
}
